import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { notify } from '@/lib/notify';
import { Button } from '@ai-tutor/ui';
import { Loader2, MessageSquare, Sidebar, X } from 'lucide-react';
import { ChatInterface } from './ChatInterface';
import { ConversationSelector } from './ConversationSelector';
import { PanelBoundary } from '@/components/core/common/boundaries/PanelBoundary';
import { PanelLoading } from '@/components/features/learning/common/PanelLoading';
import { ErrorState } from '@/components/core/common/boundaries/ErrorState';
import { lessonApi, type ConversationSummary } from '@/lib/api-client';
import type { StepView } from '@/types/pipeline';
import { queryKeys } from '@/config/query-client';

// How long to wait before the second invalidation of the classified-chat
// query after a chat turn completes. The classifier runs as a fire-and-
// forget background task on the backend AFTER the stream ends, so an
// immediate refetch can miss the label update. The first invalidation
// fires right after the stream finishes (cheap; might be stale); the
// second fires after this delay to catch the classifier's write.
const CLASSIFIED_CHAT_REFRESH_DELAY_MS = 2000;

interface DoubtsExplorePanelProps {
  lessonId?: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  /**
   * Set when the chat is shown in a sheet (phone / tablet): the header's
   * collapse toggle becomes a close button.
   */
  onClose?: () => void;
  /**
   * The lesson's current version (bare, e.g. "1.0"). Used as a refetch
   * trigger so that switching the lesson version reloads the chat list
   * scoped to that version - old-version chats stay parked on their
   * version instead of bleeding into the new one's panel.
   */
  currentVersion?: string;
  /**
   * Per-step view map threaded down from the page-level poll. Used to gate
   * the chat input on `byte_selection === 'completed'` - chat needs the
   * lesson context to ground answers, so before that we show a "Lesson is
   * being prepared…" state instead of letting the user fire blind questions.
   */
  steps?: Record<string, StepView>;
  /**
   * True once the lesson's pipeline is terminal (completed/failed). Lets the
   * chat tell "context is still coming" apart from "context failed and is
   * never coming" - so a failed foundation shows an honest unavailable state
   * instead of a forever "ready soon" spinner.
   */
  lessonDone?: boolean;
  /**
   * True when a large source is paused awaiting chapter selection. The tutor
   * can't ground on content that isn't ingested yet, so the panel shows a
   * purposeful "asleep until you pick chapters" state instead of the chat.
   */
  awaitingScope?: boolean;
  /** Reopen the chapter-selection dialog from the chat's asleep state. */
  onOpenScope?: () => void;
}

/**
 * Right-side panel for the lesson - scoped to chat only.
 *
 * Owns the conversation list + active-conversation state so the
 * heading can host a single, top-level dropdown that doubles as the
 * "+ New chat" entry point. `ChatInterface` receives the active id
 * via prop and notifies us when a stream completes (so the
 * conversation summaries refresh - title, count, recency).
 *
 * Doubts and Deep Dives that used to live behind a view-selector here
 * have been relocated:
 *  - Doubts → Appendix tab in `ReferenceViewer`
 *  - Deep Dives → "Suggested deep-dives" section in `ExtrasManager`
 */
export function DoubtsExplorePanel({
  lessonId,
  isCollapsed = false,
  onToggleCollapse,
  onClose,
  currentVersion,
  steps,
  lessonDone,
  awaitingScope = false,
  onOpenScope,
}: DoubtsExplorePanelProps) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  // Null until the bootstrap effect lands the first list (or creates a
  // conversation when none exist). Used as the gate that lets
  // `ChatInterface` start fetching messages + accepting input.
  const [activeConversationId, setActiveConversationId] = useState<
    string | null
  >(null);
  // False until the conversation-list bootstrap below resolves. We hold a
  // spinner until then rather than mounting ChatInterface with a transient
  // null conversation id, which would briefly flash the "Start a
  // conversation" welcome before the real history loads.
  const [bootstrapDone, setBootstrapDone] = useState(false);
  // The list request failed. Rendering the chat anyway left a live input with
  // no conversation behind it - Send was enabled and did nothing - so a failed
  // bootstrap gets its own state with a way to try again.
  const [bootstrapFailed, setBootstrapFailed] = useState(false);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);

  const queryClient = useQueryClient();
  // Track the delayed second invalidation so we can cancel it on
  // unmount (or when a new turn fires before the previous one's delay
  // resolves) and avoid setting state on an unmounted component.
  const classifiedRefreshTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    return () => {
      if (classifiedRefreshTimerRef.current) {
        clearTimeout(classifiedRefreshTimerRef.current);
      }
    };
  }, []);

  // Bootstrap: list the lesson's conversations (scoped to the current
  // version), pick the most recent or create one so the user lands on a
  // usable thread without a manual "+ New chat" click. `currentVersion`
  // is in the dep array so a version switch refetches and shows the
  // right slice of chats - old-version threads stay parked on their
  // version instead of bleeding through.
  useEffect(() => {
    if (!lessonId) return;
    let cancelled = false;
    // Re-resolving (initial mount or a version switch) - show the spinner
    // again until the right conversation lands.
    setBootstrapDone(false);
    setBootstrapFailed(false);
    (async () => {
      try {
        const data = await lessonApi.listConversations(lessonId);
        if (cancelled) return;
        if (data.conversations.length > 0) {
          setConversations(data.conversations);
          setActiveConversationId(data.conversations[0].conversation_id);
        } else {
          const created = await lessonApi.createConversation(lessonId);
          if (cancelled) return;
          setConversations([created]);
          setActiveConversationId(created.conversation_id);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to list chat conversations', err);
        if (!cancelled) setBootstrapFailed(true);
      } finally {
        if (!cancelled) setBootstrapDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lessonId, currentVersion, bootstrapAttempt]);

  const refreshConversations = async () => {
    if (!lessonId) return;
    try {
      const data = await lessonApi.listConversations(lessonId);
      setConversations(data.conversations);
    } catch {
      /* non-critical - dropdown will reconcile on next mount */
    }

    // Refresh the lesson-wide classified-chat aggregate so the Appendix's
    // Doubts tab and Extras' Suggested Deep-Dives reflect this turn. The
    // classifier runs as a background task on the backend after the
    // stream ends, so we invalidate twice: once now (catches the case
    // where the new label is already written / unchanged) and once
    // after a short delay to catch the classifier's actual write.
    const key = queryKeys.lessons.classifiedChat(lessonId);
    queryClient.invalidateQueries({ queryKey: key });

    if (classifiedRefreshTimerRef.current) {
      clearTimeout(classifiedRefreshTimerRef.current);
    }
    classifiedRefreshTimerRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: key });
      classifiedRefreshTimerRef.current = null;
    }, CLASSIFIED_CHAT_REFRESH_DELAY_MS);
  };

  const handleNewChat = async () => {
    if (!lessonId) return;
    try {
      const created = await lessonApi.createConversation(lessonId);
      // Optimistic prepend so the dropdown updates instantly. The
      // post-stream refresh later reconciles with server state.
      setConversations(prev => [created, ...prev]);
      setActiveConversationId(created.conversation_id);
    } catch (err: any) {
      notify.error(err, "Couldn't start a new chat");
    }
  };

  const handleSelectConversation = (conversationId: string) => {
    if (conversationId === activeConversationId) return;
    setActiveConversationId(conversationId);
  };

  const handleDeleteConversation = async (conversationId: string) => {
    if (!lessonId) return;
    try {
      await lessonApi.deleteConversation(lessonId, conversationId);
      const remaining = conversations.filter(
        c => c.conversation_id !== conversationId
      );
      setConversations(remaining);
      // If we just deleted the active one, fall through to the next-
      // most-recent. If the list is now empty, create a fresh thread
      // so the input area never gets stuck without a target.
      if (conversationId === activeConversationId) {
        if (remaining.length > 0) {
          setActiveConversationId(remaining[0].conversation_id);
        } else {
          const created = await lessonApi.createConversation(lessonId);
          setConversations([created]);
          setActiveConversationId(created.conversation_id);
        }
      }
    } catch (err: any) {
      notify.error(err, "Couldn't delete conversation");
    }
  };

  const activeConversation = conversations.find(
    c => c.conversation_id === activeConversationId
  );

  // Collapsed view - just the panel toggle. The view-icon shortcuts
  // (Chat / Doubts / Deep Dives) are gone with the views.
  if (isCollapsed) {
    return (
      <div className='flex h-full flex-col items-center space-y-3 py-4'>
        <Button
          variant='ghost'
          size='sm'
          onClick={onToggleCollapse}
          aria-label='Expand chat panel'
          className='h-10 w-10 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
        >
          <Sidebar className='h-4 w-4' />
        </Button>
        <div
          className='relative flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border border-brand-200 bg-brand-50 hover:bg-brand-100 dark:border-primary dark:bg-muted dark:hover:bg-muted'
          title='Chat'
          onClick={onToggleCollapse}
        >
          <MessageSquare className='h-4 w-4 text-brand-600 dark:text-primary' />
          <div className='absolute -left-1 top-1/2 h-4 w-1 -translate-y-1/2 rounded-r-full bg-brand-600 dark:bg-primary' />
        </div>
      </div>
    );
  }

  // Chat grounds on the lesson context (byte_selection), NOT on the content
  // artifacts - so it unlocks the moment context is ready, letting the user
  // ask questions while text/mindmap still generate. Three states:
  //   ready       - context completed (or off-flow lesson with no context step)
  //   unavailable - the foundation terminally failed (context failed, or the
  //                 lesson is terminal but context never completed, e.g. all
  //                 sources failed). Chat can't be grounded and never will be
  //                 here, so say so instead of a forever "ready soon" spinner.
  //   pending     - context is still being built (the honest "ready soon").
  // The retry for a failure lives in the center build view, not here.
  const byte = steps?.byte_selection?.state;
  const chatReady =
    steps !== undefined && (byte === undefined || byte === 'completed');
  const chatUnavailable =
    !chatReady &&
    (byte === 'failed' || (lessonDone === true && byte !== 'completed'));

  return (
    <div className='flex h-full flex-col'>
      {/* Top bar - conversation history + new-chat dropdown on the
          left, panel collapse control on the right. Padding + button
          heights mirror the source panel header (`p-3` outer + `h-8`
          inner) so all three lesson-page headers (lesson nav, source
          panel, chat panel) land at the same rendered height. */}
      <div className='flex flex-shrink-0 items-center gap-1 border-b border-border p-3'>
        <div className='min-w-0 flex-1'>
          {lessonId && (
            <ConversationSelector
              conversations={conversations}
              activeConversation={activeConversation}
              onSelect={handleSelectConversation}
              onNewChat={handleNewChat}
              onDelete={handleDeleteConversation}
            />
          )}
        </div>
        {onClose ? (
          <Button
            variant='ghost'
            size='sm'
            onClick={onClose}
            className='tap-target h-8 w-8 flex-shrink-0 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
            aria-label='Close chat'
          >
            <X className='h-5 w-5' />
          </Button>
        ) : (
          <Button
            variant='ghost'
            size='sm'
            onClick={onToggleCollapse}
            className='h-8 w-8 flex-shrink-0 p-0 text-muted-foreground hover:bg-muted hover:text-foreground'
            title='Collapse panel'
            aria-label='Collapse chat panel'
          >
            <Sidebar className='h-4 w-4' />
          </Button>
        )}
      </div>

      <div className='min-h-0 flex-1 overflow-hidden'>
        {awaitingScope ? (
          // A source is paused for the student to choose what to use (it is
          // over its per-file limit or the month's pages) - on a first build
          // or on a lesson that already has content. The tutor waits for it,
          // and this is the one way back to the picker (there is no top
          // banner), so it is a real button, not a soft link.
          <div className='flex h-full flex-col items-center justify-center px-6 text-center'>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
              <MessageSquare className='h-5 w-5 text-muted-foreground' />
            </div>
            <p className='mt-4 text-sm font-medium text-foreground'>
              Your tutor is waiting on your choice
            </p>
            <p className='mt-1 max-w-xs text-sm text-muted-foreground'>
              Choose what to use from your file and I&apos;ll explain it, quiz
              you, and answer anything about it.
            </p>
            {onOpenScope && (
              <Button size='sm' className='mt-4' onClick={onOpenScope}>
                Choose what to use
              </Button>
            )}
          </div>
        ) : chatReady ? (
          bootstrapFailed ? (
            <ErrorState
              variant='panel'
              title="Couldn't load your chats"
              description='Check your connection and try again.'
              primaryAction={{
                label: 'Try again',
                onClick: () => setBootstrapAttempt(n => n + 1),
              }}
            />
          ) : activeConversationId || bootstrapDone ? (
            <PanelBoundary name='Chat'>
              <ChatInterface
                lessonId={lessonId}
                activeConversationId={activeConversationId}
                onMessageSent={refreshConversations}
              />
            </PanelBoundary>
          ) : (
            // Resolving which conversation to open - a single spinner (same
            // style ChatInterface uses for its own history fetch) so it reads
            // as one continuous load, not a welcome-then-spinner flash.
            <div className='flex h-full items-center justify-center'>
              <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
            </div>
          )
        ) : chatUnavailable ? (
          // Foundation failed - honest, calm, no spinner. The retry lives in
          // the center panel, so we just point there (no button here).
          <div className='flex h-full flex-col items-center justify-center px-6 text-center'>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-muted'>
              <MessageSquare className='h-5 w-5 text-muted-foreground' />
            </div>
            <p className='mt-4 text-sm font-medium text-foreground'>
              Chat isn&apos;t available yet
            </p>
            <p className='mt-1 max-w-xs text-sm text-muted-foreground'>
              This lesson didn&apos;t finish setting up. Retry it from the main
              panel and chat will turn on.
            </p>
          </div>
        ) : (
          <PanelLoading
            icon={MessageSquare}
            title='Chat will be ready soon'
            description='You can chat with your lesson once it has been set up.'
          />
        )}
      </div>
    </div>
  );
}
